/**
 * API de sincronização do admin com o backend (DynamoDB).
 * - GET /api/admin/catalog — lê tabelas Dynamo e hidrata o painel (ver AdminCatalogProvider).
 * - POST /api/admin/sync — envia snapshot completo (localStorage) para gravação no Dynamo.
 * - Destino: `VITE_ADMIN_SYNC_URL` quando definida; senão mesma origem que `apiUrl` (proxy local).
 */

import { adminIngestApiUrl } from './adminIngestApi';
import { apiUrl } from './api';
import { adminApiHeaders } from './adminApiAuth';
import { getAdminProfileExtras } from './adminProfileApi';
import {
  getCidades,
  getCategorias,
  getSubcategorias,
  getExperiencias,
  getTarifarios,
  getTarifarioGlobalConfig,
  getRegioes,
  getTags,
  getPontosEncontro,
} from './experienciasStorage';
import { getAssociacoes } from './associacoesStorage';
import { getGuias, getAgencias } from './guiasAgenciasStorage';
import { getClientes } from './clientesStorage';
import { getSolicitacoes } from './solicitacoesFornecedorStorage';
import {
  getNewsArticles,
  getNewsCategories,
  getNewsSubcategories,
  getNewsLimitsByCity,
  getGuiaRegisteredUsersForSync,
} from './newsStorage';
import { getHospedagens } from './hospedagemStorage';
import { getEvents, getEventCategories, getEventTags } from './eventsStorage';
import { getEstabelecimentos } from './estabelecimentosStorage';
import { getEstabelecimentoCategories, getEstabelecimentoSubcategories } from './estabelecimentosCategoriasStorage';
import { getCupons } from './cuponsStorage';
import { getComercioProfiles } from './comercioProfilesStorage';
import { getPixConfig } from './financeiroStorage';
import { getGuiaConvitesPendentesForSync } from './guiaConvitesLocalStorage';
import { getExcursoesFromStorage } from '../../../shared/lib/excursaoStorage';

/** Snapshot completo. A API deve fazer upsert dos campos que reconhecer e ignorar o restante. */
export interface AdminSyncPayload {
  payloadVersion: number;
  cidades: unknown[];
  categorias: unknown[];
  subcategorias: unknown[];
  experiencias: unknown[];
  tarifarios: unknown[];
  tarifarioGlobal: unknown;
  regioes: unknown[];
  tags: unknown[];
  pontosEncontro: unknown[];
  guias: unknown[];
  agencias: unknown[];
  associacoes: unknown[];
  clientes: unknown[];
  solicitacoesFornecedor: unknown[];
  newsArticles: unknown[];
  newsCategories: unknown[];
  newsSubcategories: unknown[];
  newsLimitsByCity: unknown;
  guiaRegisteredUsers: unknown;
  hospedagens: unknown[];
  events: unknown[];
  eventCategories: unknown[];
  eventTags: unknown[];
  estabelecimentos: unknown[];
  estabelecimentoCategorias: unknown[];
  estabelecimentoSubcategorias: unknown[];
  cupons: unknown[];
  comercioProfiles: unknown;
  pixConfig: unknown;
  guiaConvitesPendentes: unknown[];
  adminProfileExtras: unknown;
  /** Excursões (mapa em storage → array para Dynamo); opcional em respostas antigas do GET catalog */
  excursoes?: unknown[];
  /** Layout da home Experiências no app cliente (Dynamo documento único) */
  clienteHomeLayout?: unknown;
}

const PAYLOAD_VERSION = 2;

/** Monta o payload completo do admin para envio à API */
export function buildAdminSyncPayload(): AdminSyncPayload {
  return {
    payloadVersion: PAYLOAD_VERSION,
    cidades: getCidades(),
    categorias: getCategorias(),
    subcategorias: getSubcategorias(),
    experiencias: getExperiencias(),
    tarifarios: getTarifarios(),
    tarifarioGlobal: getTarifarioGlobalConfig(),
    regioes: getRegioes(),
    tags: getTags(),
    pontosEncontro: getPontosEncontro(),
    guias: getGuias(),
    agencias: getAgencias(),
    associacoes: getAssociacoes(),
    clientes: getClientes(),
    solicitacoesFornecedor: getSolicitacoes(),
    newsArticles: getNewsArticles(),
    newsCategories: getNewsCategories(),
    newsSubcategories: getNewsSubcategories(),
    newsLimitsByCity: getNewsLimitsByCity(),
    guiaRegisteredUsers: getGuiaRegisteredUsersForSync(),
    hospedagens: getHospedagens(),
    events: getEvents(),
    eventCategories: getEventCategories(),
    eventTags: getEventTags(),
    estabelecimentos: getEstabelecimentos(),
    estabelecimentoCategorias: getEstabelecimentoCategories(),
    estabelecimentoSubcategorias: getEstabelecimentoSubcategories(),
    cupons: getCupons(),
    comercioProfiles: getComercioProfiles(),
    pixConfig: getPixConfig(),
    guiaConvitesPendentes: getGuiaConvitesPendentesForSync(),
    adminProfileExtras: getAdminProfileExtras(),
    excursoes: Object.values(getExcursoesFromStorage()),
  };
}

/** JSON formatado (ex.: salvar em `exports/admin-sync-payload.json` para CI). */
export function serializeAdminSyncPayload(): string {
  return `${JSON.stringify(buildAdminSyncPayload(), null, 2)}\n`;
}

/** Evita que a UI fique congelada minutos se a API (ex.: DynamoDB) estiver lenta. */
const SYNC_FETCH_TIMEOUT_MS = 90_000;

function scheduleAfterPaint(fn: () => void): void {
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(() => fn(), { timeout: 2000 });
    return;
  }
  setTimeout(fn, 0);
}

/**
 * Envia o snapshot atual do admin (conteúdo em localStorage) para a API.
 * Retorna true se a API aceitou, false em caso de erro ou API indisponível.
 */
export async function pushAdminSyncToApi(): Promise<{ ok: boolean; error?: string }> {
  const controller = new AbortController();
  const kill = setTimeout(() => controller.abort(), SYNC_FETCH_TIMEOUT_MS);
  try {
    const payload = buildAdminSyncPayload();
    const body = JSON.stringify(payload);
    const res = await fetch(adminIngestApiUrl('/api/admin/sync'), {
      method: 'POST',
      headers: adminApiHeaders({ 'Content-Type': 'application/json' }),
      body,
      signal: controller.signal,
    });
    const text = await res.text();
    if (!res.ok) {
      return { ok: false, error: text || `Erro ${res.status}` };
    }
    // 207 = sync parcial (ex.: falha em alguma tabela Dynamo); `fetch` considera ok, mas não é sucesso total.
    if (res.status === 207) {
      try {
        const partial = JSON.parse(text) as { errors?: string[]; message?: string };
        const errList = Array.isArray(partial.errors) ? partial.errors.join('; ') : '';
        return {
          ok: false,
          error: errList || partial.message || 'Sincronização com erros parciais no servidor.',
        };
      } catch {
        return { ok: false, error: 'Sincronização com erros parciais (HTTP 207).' };
      }
    }
    let json: unknown;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      return {
        ok: false,
        error:
          'A resposta do servidor não é JSON (muitas vezes é a página HTML do site). No build do admin, defina VITE_API_URL ou VITE_ADMIN_SYNC_URL com a origem da API (ex.: https://api.naviexperience.com), sem /api no fim.',
      };
    }
    if (json == null || typeof json !== 'object') {
      return { ok: false, error: 'Resposta vazia ou inválida do endpoint de sync.' };
    }
    return { ok: true };
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      return { ok: false, error: `Tempo esgotado (${SYNC_FETCH_TIMEOUT_MS / 1000}s). A API pode estar lenta ou sobrecarregada.` };
    }
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Erro ao conectar com a API',
    };
  } finally {
    clearTimeout(kill);
  }
}

/**
 * Agenda o sync após o browser pintar / ficar ocioso — não bloqueia cliques nem fechamento de modais.
 * Use após gravar em localStorage; mostre aviso só em `onComplete` se `ok === false`.
 */
export function pushAdminSyncToApiDeferred(onComplete?: (result: { ok: boolean; error?: string }) => void): void {
  scheduleAfterPaint(() => {
    void pushAdminSyncToApi().then((result) => {
      onComplete?.(result);
    });
  });
}

/**
 * Verifica se a API está disponível e responde.
 * Usa /api/excursoes como probe (endpoint comum da API).
 */
const HEALTH_CHECK_TIMEOUT_MS = 12_000;

export async function checkApiHealth(): Promise<boolean> {
  const controller = new AbortController();
  const kill = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT_MS);
  try {
    const res = await fetch(apiUrl('/api/excursoes'), { method: 'GET', signal: controller.signal });
    return res.status < 500; // Considera ok se não for erro de servidor
  } catch {
    return false;
  } finally {
    clearTimeout(kill);
  }
}
